import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    getAgentChats,
    createChat,
    deleteChat,
    searchChats
} from '../../services/adamConciergeService';

const AdamChatSidebar = ({
    isOpen,
    onToggle,
    agentId,
    activeChatId,
    onSelectChat,
    onNewChat,
    refreshTrigger
}) => {
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const searchTimeoutRef = useRef(null);

    // Fetch agent's chats
    const fetchChats = useCallback(async () => {
        if (!agentId) return;

        try {
            setLoading(true);
            const response = await getAgentChats(agentId);
            if (response.success) {
                setChats(response.data || []);
            }
        } catch (error) {
            // Error fetching chats
        } finally {
            setLoading(false);
        }
    }, [agentId]);

    useEffect(() => {
        if (isOpen) fetchChats();
    }, [isOpen, fetchChats]);

    useEffect(() => {
        if (refreshTrigger && isOpen) {
            const timer = setTimeout(() => fetchChats(), 500);
            return () => clearTimeout(timer);
        }
    }, [refreshTrigger, isOpen, fetchChats]);

    // Search with debounce
    const handleSearch = useCallback(async (query) => {
        if (!agentId) return;
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (!query.trim()) {
            fetchChats();
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                setIsSearching(true);
                const response = await searchChats(agentId, query);
                if (response.success) {
                    setChats(response.data || []);
                }
            } catch (error) {
                // Error searching
            } finally {
                setIsSearching(false);
            }
        }, 300);
    }, [agentId, fetchChats]);

    const onSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        handleSearch(query);
    };

    // New chat
    const handleNewChat = async () => {
        if (!agentId) return;
        try {
            const response = await createChat();
            if (response.success) {
                await fetchChats();
                if (onNewChat) onNewChat(response.data.chatId, response.data);
            }
        } catch (error) {
            // Error creating chat
        }
    };

    // Delete chat
    const handleDeleteChat = async (chatId) => {
        if (!agentId) return;
        try {
            await deleteChat(chatId, agentId);
            setChats(prev => prev.filter(chat => chat._id !== chatId));
            setShowDeleteConfirm(null);

            if (chatId === activeChatId) {
                const remaining = chats.filter(c => c._id !== chatId);
                if (remaining.length > 0) {
                    onSelectChat(remaining[0]._id);
                } else {
                    handleNewChat();
                }
            }
        } catch (error) {
            // Error deleting chat
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Group chats by date
    const groupedChats = chats.reduce((groups, chat) => {
        const dateKey = formatDate(chat.updatedAt || chat.createdAt);
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(chat);
        return groups;
    }, {});

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ x: -288, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -288, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed left-0 top-11 md:top-12 h-[calc(100dvh-2.75rem)] md:h-[calc(100dvh-3rem)] w-[min(18rem,85vw)] max-w-[85vw] bg-white border-r border-gray-200 shadow-xl z-40 flex flex-col min-h-0"
                    >
                        {/* Header */}
                        <div className="border-b border-gray-100 bg-white">
                            <div className="flex items-center justify-between px-4 py-3">
                                <h2 className="text-sm font-medium text-gray-900 uppercase tracking-wider">Chat History</h2>
                                <motion.button
                                    onClick={onToggle}
                                    className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </motion.button>
                            </div>

                            <div className="px-4 pb-3">
                                <button
                                    onClick={handleNewChat}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-black text-white rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    New Chat
                                </button>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="p-3 border-b border-gray-100">
                            <div className="relative">
                                <svg
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search chats..."
                                    value={searchQuery}
                                    onChange={onSearchChange}
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all"
                                />
                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chat List */}
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2">
                            {loading ? (
                                <div className="flex items-center justify-center h-32">
                                    <div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                                </div>
                            ) : chats.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                                    <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <span className="text-sm font-light">No chats yet</span>
                                </div>
                            ) : (
                                <div className="py-2">
                                    {Object.entries(groupedChats).map(([dateGroup, groupChats]) => (
                                        <div key={dateGroup}>
                                            <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                {dateGroup}
                                            </div>
                                            {groupChats.map((chat) => (
                                                <div
                                                    key={chat._id}
                                                    className={`group relative mx-2 mb-1 rounded-lg transition-all ${chat._id === activeChatId ? 'bg-gray-100' : 'hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <button
                                                        onClick={() => onSelectChat(chat._id)}
                                                        className="w-full text-left px-3 py-3 pr-10"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <svg
                                                                className={`w-4 h-4 flex-shrink-0 ${chat._id === activeChatId ? 'text-black' : 'text-gray-400'}`}
                                                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                            </svg>
                                                            <span className={`text-sm truncate ${chat._id === activeChatId ? 'font-medium text-black' : 'font-light text-gray-700'}`}>
                                                                {chat.title}
                                                            </span>
                                                        </div>
                                                    </button>

                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(chat._id); }}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-all"
                                                    >
                                                        <svg className="w-4 h-4 text-gray-400 hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>

                                                    <AnimatePresence>
                                                        {showDeleteConfirm === chat._id && (
                                                            <motion.div
                                                                initial={{ opacity: 0, scale: 0.9 }}
                                                                animate={{ opacity: 1, scale: 1 }}
                                                                exit={{ opacity: 0, scale: 0.9 }}
                                                                className="absolute inset-0 bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center gap-2 z-10"
                                                            >
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteChat(chat._id); }}
                                                                    className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-md hover:bg-red-600 transition-colors"
                                                                >
                                                                    Delete
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(null); }}
                                                                    className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-3 border-t border-gray-100 bg-gray-50">
                            <div className="text-xs text-gray-400 text-center font-light">
                                {chats.length} {chats.length === 1 ? 'conversation' : 'conversations'}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onToggle}
                        className="fixed inset-0 bg-black/20 z-30 md:hidden"
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default AdamChatSidebar;
